import { registerSiteContent } from '@/features/siteContent/runtime';

/**
 * Hero artwork in `public/banners`. Each dedicated banner is a finished visual
 * with the page's headline, lede and proof points already set into the image,
 * so a page that has one must not repeat that copy in markup beside it -
 * see `hasDedicatedBanner`. Everything else falls back to `common.webp`, which
 * is generic platform artwork and duplicates no page in particular.
 */
const DEDICATED_BANNERS = new Set([
  'ai-assistant',
  'checkout',
  'custom-domains',
  'customer-accounts',
  'online-store',
  'share-links',
  'social-selling',
  'store-builder',
  'themes',
  'whatsapp-commerce',
  'why-shoplaunch',
]);

/** True when the banner carries the page's own headline and supporting copy. */
export const hasDedicatedBanner = (slug: string) => DEDICATED_BANNERS.has(slug);

const bannerName = (slug: string) => (hasDedicatedBanner(slug) ? slug : 'common');

export const bannerSrc = (slug: string) => `/banners/${bannerName(slug)}.webp`;

/** The 10:7 phone counterpart of `bannerSrc` under `public/banners/mobile`.
 *  Every desktop banner has one, so the same fallback applies. */
export const mobileBannerSrc = (slug: string) => `/banners/mobile/${bannerName(slug)}.webp`;

export interface HeroSlide {
  /** Path under `public/banners`, spelled out rather than built from the slug:
   *  `bannerSrc` answers "which banner does this page get" and falls back to
   *  `common.webp`, which would silently show the wrong art for `price`. */
  src: string;
  /** Where a click goes. The artwork carries that page's headline, so a slide
   *  has to link where its own words point. */
  href: string;
  /** What the banner says - it is the image's only text, so `alt` carries it. */
  alt: string;
  /** The phone artwork under `public/banners/mobile` - drawn at 10:7 so its
   *  type is readable at phone width. Shown below the `sm` breakpoint. */
  mobileSrc: string;
}

/**
 * The home page hero rotation. `common.webp` leads because it is the one banner
 * that sells the platform rather than a single feature, and `price.webp` closes
 * on the plans. `razorPay.webp` is deliberately absent: it is dashboard artwork
 * for connecting a gateway, not a marketing banner.
 */
export const HERO_SLIDES: HeroSlide[] = [
  {
    src: '/banners/common.webp',
    mobileSrc: '/banners/mobile/common.webp',
    href: '/register',
    alt: 'Build, launch and grow your online business - the all-in-one NeonCarts platform, for every industry',
  },
  {
    src: '/banners/why-shoplaunch.webp',
    mobileSrc: '/banners/mobile/why-shoplaunch.webp',
    href: '/why-shoplaunch',
    alt: 'The commerce platform that works the way India sells - storefront, orders, payments and WhatsApp in one place',
  },
  {
    src: '/banners/store-builder.webp',
    mobileSrc: '/banners/mobile/store-builder.webp',
    href: '/features/store-builder',
    alt: 'NeonCarts store builder - a polished storefront, no code needed',
  },
  {
    src: '/banners/themes.webp',
    mobileSrc: '/banners/mobile/themes.webp',
    href: '/features/themes',
    alt: 'Themes and design - looks made for Indian D2C brands',
  },
  {
    src: '/banners/online-store.webp',
    mobileSrc: '/banners/mobile/online-store.webp',
    href: '/features/online-store',
    alt: 'The NeonCarts online store - your products, categories and cart on your own storefront',
  },
  {
    src: '/banners/custom-domains.webp',
    mobileSrc: '/banners/mobile/custom-domains.webp',
    href: '/features/custom-domains',
    alt: 'Custom domains - sell on your own .in or .com',
  },
  {
    src: '/banners/whatsapp-commerce.webp',
    mobileSrc: '/banners/mobile/whatsapp-commerce.webp',
    href: '/features/whatsapp-commerce',
    alt: 'WhatsApp Commerce - take orders, confirm them and send updates on WhatsApp',
  },
  {
    src: '/banners/social-selling.webp',
    mobileSrc: '/banners/mobile/social-selling.webp',
    href: '/features/social-selling',
    alt: 'Social and marketplaces - turn followers on Instagram and Facebook into customers',
  },
  {
    src: '/banners/share-links.webp',
    mobileSrc: '/banners/mobile/share-links.webp',
    href: '/features/share-links',
    alt: 'Links and QR codes - share your catalogue anywhere a customer already is',
  },
  {
    src: '/banners/checkout.webp',
    mobileSrc: '/banners/mobile/checkout.webp',
    href: '/features/checkout',
    alt: 'NeonCarts checkout - UPI, cards, netbanking and cash on delivery, built for conversions',
  },
  {
    src: '/banners/customer-accounts.webp',
    mobileSrc: '/banners/mobile/customer-accounts.webp',
    href: '/features/customer-accounts',
    alt: 'Customer accounts - sign-in, order history and saved details',
  },
  {
    src: '/banners/ai-assistant.webp',
    mobileSrc: '/banners/mobile/ai-assistant.webp',
    href: '/features/ai-assistant',
    alt: 'AI assistant - product copy, SEO titles and campaign drafts written for your catalogue',
  },
  {
    src: '/banners/price.webp',
    mobileSrc: '/banners/mobile/price.webp',
    href: '/pricing',
    alt: 'You have got big goals and NeonCarts has the platform to match - see the plans and start free today',
  },
];

registerSiteContent('home.banners', { HERO_SLIDES });
